import { useEffect, useState } from "react";
import { InputNumber, Slider } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUtensils, faDollarSign } from "@fortawesome/free-solid-svg-icons";
import "./MinimumThresholds.scss";
import { BaseFilterProps } from "interfaces/filter-component-base";

type FollowerCategory = "nano" | "micro" | "macro" | "mega" | "mid";
type EngagementCategory = "low" | "fair" | "great" | "outstanding";

interface ThresholdValues {
  minAmountFollowers: number;
  minAmountEngagement: number;
}

const FOLLOWER_CATEGORIES = {
  nano: {
    min: 1_000,
    max: 10_000,
    label: "Nano",
    range: "1K-10K",
    utensils: 1,
    dollars: 0,
  },
  micro: {
    min: 10_000,
    max: 100_000,
    label: "Micro",
    range: "10K-100K",
    utensils: 1,
    dollars: 1,
  },
  mid: {
    min: 100_000,
    max: 250_000,
    label: "Mid",
    range: "100K-250K",
    utensils: 1,
    dollars: 2,
  },
  macro: {
    min: 250_000,
    max: 1_000_000,
    label: "Macro",
    range: "250K-1M",
    utensils: 1,
    dollars: 3,
  },
  mega: {
    min: 1_000_000,
    max: null,
    label: "Mega",
    range: "1M+",
    utensils: 0,
    dollars: 5,
  },
};

const ENGAGEMENT_CATEGORIES = {
  low: { min: 0, max: 0.5, label: "Low", range: "< 0.5%" },
  fair: { min: 0.5, max: 1.5, label: "Fair", range: "0.5%-1.5%" },
  great: { min: 1.5, max: 3, label: "Great", range: "1.5%-3%" },
  outstanding: { min: 3, max: 5, label: "Outstanding", range: "3%+" },
};

const getFollowerCategory = (value: number): FollowerCategory => {
  if (value < 10_000) return "nano";
  if (value < 100_000) return "micro";
  if (value < 250_000) return "mid";
  if (value < 1_000_000) return "macro";
  return "mega";
};

const getEngagementCategory = (value: number): EngagementCategory => {
  if (value < 0.5) return "low";
  if (value < 1.5) return "fair";
  if (value < 3) return "great";
  return "outstanding";
};

const MinimumThresholds = (props: BaseFilterProps) => {
  const { onChangeValues, values } = props;

  const [thresholdValues, setThresholdValues] = useState<ThresholdValues>({
    minAmountFollowers: values?.minAmountFollowers || 0,
    minAmountEngagement: values?.minAmountEngagement || 0,
  });

  useEffect(() => {
    setThresholdValues({
      minAmountFollowers: values?.minAmountFollowers || 0,
      minAmountEngagement: values?.minAmountEngagement || 0,
    });
  }, [values]);

  const updateValues = (newValues: ThresholdValues) => {
    setThresholdValues(newValues);
    if (onChangeValues) {
      onChangeValues({
        minAmountFollowers:
          newValues.minAmountFollowers === 0
            ? null
            : newValues.minAmountFollowers,
        minAmountEngagement:
          newValues.minAmountEngagement === 0
            ? null
            : newValues.minAmountEngagement,
      });
    }
  };

  const handleFollowersChange = (value: number | null) => {
    if (value !== null && value >= 0) {
      const clampedValue = Math.max(0, Math.min(value, 10000000));
      updateValues({
        ...thresholdValues,
        minAmountFollowers: clampedValue,
      });
    }
  };

  const handleEngagementChange = (value: number | null) => {
    if (value !== null && value >= 0) {
      const clampedValue = Math.max(0, Math.min(value, 5));
      updateValues({
        ...thresholdValues,
        minAmountEngagement: clampedValue,
      });
    }
  };

  const handleFollowerCategoryClick = (category: FollowerCategory) => {
    const categoryData = FOLLOWER_CATEGORIES[category];

    updateValues({
      ...thresholdValues,
      minAmountFollowers: categoryData.min,
    });
  };

  const handleEngagementCategoryClick = (category: EngagementCategory) => {
    const categoryData = ENGAGEMENT_CATEGORIES[category];
    updateValues({
      ...thresholdValues,
      minAmountEngagement: categoryData.min,
    });
  };

  const handleFollowersSliderChange = (value: number) => {
    handleFollowersChange(value);
  };

  const handleEngagementSliderChange = (value: number) => {
    handleEngagementChange(value);
  };

  const currentFollowerCategory = getFollowerCategory(
    thresholdValues.minAmountFollowers
  );
  const currentEngagementCategory = getEngagementCategory(
    thresholdValues.minAmountEngagement
  );

  return (
    <div className="minimum-thresholds">
      <div className="threshold-section">
        <h3 className="threshold-title">Minimum Followers</h3>

        <div className="threshold-slider-container">
          <Slider
            min={0}
            max={1000000}
            step={1000}
            value={thresholdValues.minAmountFollowers}
            onChange={handleFollowersSliderChange}
            tooltip={{
              formatter: (value) => {
                if (value !== undefined) {
                  return value.toLocaleString("en-US");
                }
                return "";
              },
            }}
          />
        </div>

        <div className="threshold-input-row">
          <div className="input-container">
            <InputNumber
              value={thresholdValues.minAmountFollowers}
              onChange={handleFollowersChange}
              min={0}
              max={10000000}
              step={1000}
              formatter={(value) => {
                if (!value) return "";
                const num = parseFloat(value.toString());
                return num.toLocaleString("en-US");
              }}
              parser={(value) => {
                if (!value) return 0;
                const parsed = value.replace(/,/g, "");
                return parseFloat(parsed) || 0;
              }}
              className="threshold-input"
            />
            <span className="threshold-unit">followers</span>
          </div>
          <div
            className={`threshold-category-badge ${currentFollowerCategory}`}
          >
            {FOLLOWER_CATEGORIES[currentFollowerCategory].label}
          </div>
        </div>

        <div className="threshold-categories">
          {Object.entries(FOLLOWER_CATEGORIES).map(([key, category]) => (
            <button
              key={key}
              className={`threshold-category-button ${
                currentFollowerCategory === key ? "active" : ""
              }`}
              onClick={() =>
                handleFollowerCategoryClick(key as FollowerCategory)
              }
            >
              <div className="category-label">{category.label}</div>
              <div className="category-range">{category.range}</div>
              <div className="category-icons">
                {Array.from({ length: category.utensils }).map((_, index) => (
                  <FontAwesomeIcon
                    key={`utensil-${index}`}
                    icon={faUtensils}
                    className="category-icon utensil-icon"
                  />
                ))}
                {Array.from({ length: category.dollars }).map((_, index) => (
                  <FontAwesomeIcon
                    key={`dollar-${index}`}
                    icon={faDollarSign}
                    className="category-icon dollar-icon"
                  />
                ))}
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="threshold-section">
        <h3 className="threshold-title">Minimum Engagement Rate</h3>

        <div className="threshold-slider-container">
          <Slider
            min={0}
            max={5}
            step={0.1}
            value={thresholdValues.minAmountEngagement}
            onChange={handleEngagementSliderChange}
            tooltip={{
              formatter: (value) => {
                if (value !== undefined) {
                  return `${value.toFixed(1)}%`;
                }
                return "";
              },
            }}
          />
        </div>

        <div className="threshold-input-row">
          <div className="input-container">
            <InputNumber
              value={thresholdValues.minAmountEngagement}
              onChange={handleEngagementChange}
              min={0}
              max={5}
              step={0.1}
              precision={1}
              className="threshold-input engagement"
            />
            <span className="threshold-unit">%</span>
          </div>
          <div
            className={`threshold-category-badge engagement ${currentEngagementCategory}`}
          >
            {ENGAGEMENT_CATEGORIES[currentEngagementCategory].label}
          </div>
        </div>

        <div className="threshold-categories">
          {Object.entries(ENGAGEMENT_CATEGORIES).map(([key, category]) => (
            <button
              key={key}
              className={`threshold-category-button engagement ${
                currentEngagementCategory === key ? "active" : ""
              } ${key}`}
              onClick={() =>
                handleEngagementCategoryClick(key as EngagementCategory)
              }
            >
              <div className="engagement-category-content">
                <div className="category-dot"></div>
                <div className="category-label">{category.label}</div>
              </div>
              <div className="category-range">{category.range}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MinimumThresholds;
